import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  Typography,
  Button,
  TextField,
  Tooltip,
  Box,
  FormControlLabel,
  Switch,
  Divider
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useGlobalSettingState, useComponentWebSocket } from './GlobalSettingUtils';

const DEFAULT_POSTPROCESSING = {
  action_timeout_seconds: 1800,
  subprocess_loop_timeout_seconds: 1800,
  pdf_render_node_path: ''
};

const DEFAULT_MASTER_IMPORT_MANAGER = {
  file_process_timeout_seconds: 600,
  max_time_to_process_batch: 3600,
  max_files_in_batch: 500,
  max_semaphore: 16,
  max_workers: 8,
  worker_status_log_interval_seconds: 30,
  worker_batch_check_interval_seconds: 2,
  worker_idle_sleep_seconds: 0.1,
  batch_completion_grace_seconds: 2,
  continuous_batch_pause_seconds: 5,
  queue_folder_batch_size: 1000,
  queue_folder_scan_concurrency: 8,
  queue_folder_shutdown_threshold_minutes: 30,
  status_broadcast_min_interval_seconds: 2
};

const toNumber = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const listToText = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean).join(', ');
  }
  if (typeof value === 'string') {
    return value;
  }
  return '';
};

const textToList = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }
  if (typeof value !== 'string') {
    return [];
  }
  return value
    .split(/[,\n]/)
    .map((item) => item.trim())
    .filter(Boolean);
};

const mergeSection = (incoming, defaults) => {
  const source = incoming && typeof incoming === 'object' ? incoming : {};
  return {
    ...defaults,
    ...source
  };
};

const SystemSettings = () => {
  const {
    isModified,
    setIsModified,
    t,
    openSnackbar
  } = useGlobalSettingState();

  const [systemConfig, setSystemConfig] = useState({
    allowUserPluginGeneration: true,
    paymentsEnabled: true,
    showWidgetEvidence: true,
    restrictToUserFolder: false,
    searchThreshold: 1.4,
    aiVisionMaxRetries: 5,
    maxAudioRecordingLengthSeconds: 7200,
    allowedConnectorsText: '',
    disabledConnectorsText: '',
    postprocessing: DEFAULT_POSTPROCESSING,
    master_import_manager: DEFAULT_MASTER_IMPORT_MANAGER
  });

  const applyConfigData = useCallback((data) => {
    if (!data || typeof data !== 'object') {
      return;
    }

    setSystemConfig((prev) => ({
      ...prev,
      allowUserPluginGeneration: data.allowUserPluginGeneration ?? prev.allowUserPluginGeneration,
      paymentsEnabled: data.paymentsEnabled ?? prev.paymentsEnabled,
      showWidgetEvidence: data.showWidgetEvidence ?? prev.showWidgetEvidence,
      restrictToUserFolder: data.restrictToUserFolder ?? prev.restrictToUserFolder,
      searchThreshold: data.searchThreshold ?? prev.searchThreshold,
      aiVisionMaxRetries: data.aiVisionMaxRetries ?? prev.aiVisionMaxRetries,
      maxAudioRecordingLengthSeconds: data.max_audio_recording_length_seconds ?? data.maxAudioRecordingLengthSeconds,
      allowedConnectorsText: data.allowedConnectors !== undefined ? listToText(data.allowedConnectors) : prev.allowedConnectorsText,
      disabledConnectorsText: data.disabledConnectors !== undefined ? listToText(data.disabledConnectors) : prev.disabledConnectorsText,
      postprocessing: mergeSection(data.postprocessing, prev.postprocessing),
      master_import_manager: mergeSection(data.master_import_manager, prev.master_import_manager)
    }));
  }, []);

  const commandHandlers = {
    update_configuration: (message) => {
      if (message.data === 'success') {
        openSnackbar(t('global_setting.save_completed'), 3000);
        setIsModified(false);
      }
    },
    get_performance_settings: (message) => {
      applyConfigData(message.data);
    },
    configuration_data: (message) => {
      applyConfigData(message.data);
    }
  };

  const { sendCommand } = useComponentWebSocket(commandHandlers);

  useEffect(() => {
    sendCommand('get_performance_settings');
  }, [sendCommand]);

  const updateField = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setSystemConfig((prev) => ({
      ...prev,
      [field]: value
    }));
    setIsModified(true);
  };

  const updateSectionField = (section, field, numeric = false) => (event) => {
    const value = numeric ? event.target.value : event.target.value;
    setSystemConfig((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section] || {}),
        [field]: value
      }
    }));
    setIsModified(true);
  };

  const handleToggle = (field) => (event) => {
    setSystemConfig((prev) => ({
      ...prev,
      [field]: event.target.checked
    }));
    setIsModified(true);
  };

  const renderToggleWithHelp = (field, labelKey, labelFallback, helpKey, helpFallback) => (
    <FormControlLabel
      control={<Switch checked={Boolean(systemConfig[field])} onChange={handleToggle(field)} />}
      label={
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          <Typography component="span" variant="body2">
            {t(labelKey, labelFallback)}
          </Typography>
          <Tooltip title={t(helpKey, helpFallback)} placement="top">
            <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </Box>
      }
    />
  );

  const handleSave = () => {
    if (!isModified) {
      return;
    }

    const payload = {
      allowUserPluginGeneration: systemConfig.allowUserPluginGeneration,
      paymentsEnabled: systemConfig.paymentsEnabled,
      showWidgetEvidence: systemConfig.showWidgetEvidence,
      restrictToUserFolder: systemConfig.restrictToUserFolder,
      searchThreshold: toNumber(systemConfig.searchThreshold, 1.4),
      aiVisionMaxRetries: toNumber(systemConfig.aiVisionMaxRetries, 5),
      max_audio_recording_length_seconds: toNumber(systemConfig.maxAudioRecordingLengthSeconds, 7200),
      allowedConnectors: textToList(systemConfig.allowedConnectorsText),
      disabledConnectors: textToList(systemConfig.disabledConnectorsText),
      postprocessing: {
        action_timeout_seconds: toNumber(systemConfig.postprocessing.action_timeout_seconds, DEFAULT_POSTPROCESSING.action_timeout_seconds),
        subprocess_loop_timeout_seconds: toNumber(systemConfig.postprocessing.subprocess_loop_timeout_seconds, DEFAULT_POSTPROCESSING.subprocess_loop_timeout_seconds),
        pdf_render_node_path: String(systemConfig.postprocessing.pdf_render_node_path || '').trim()
      },
      master_import_manager: {
        file_process_timeout_seconds: toNumber(systemConfig.master_import_manager.file_process_timeout_seconds, DEFAULT_MASTER_IMPORT_MANAGER.file_process_timeout_seconds),
        max_time_to_process_batch: toNumber(systemConfig.master_import_manager.max_time_to_process_batch, DEFAULT_MASTER_IMPORT_MANAGER.max_time_to_process_batch),
        max_files_in_batch: toNumber(systemConfig.master_import_manager.max_files_in_batch, DEFAULT_MASTER_IMPORT_MANAGER.max_files_in_batch),
        max_semaphore: toNumber(systemConfig.master_import_manager.max_semaphore, DEFAULT_MASTER_IMPORT_MANAGER.max_semaphore),
        max_workers: toNumber(systemConfig.master_import_manager.max_workers, DEFAULT_MASTER_IMPORT_MANAGER.max_workers),
        worker_status_log_interval_seconds: toNumber(systemConfig.master_import_manager.worker_status_log_interval_seconds, DEFAULT_MASTER_IMPORT_MANAGER.worker_status_log_interval_seconds),
        worker_batch_check_interval_seconds: toNumber(systemConfig.master_import_manager.worker_batch_check_interval_seconds, DEFAULT_MASTER_IMPORT_MANAGER.worker_batch_check_interval_seconds),
        worker_idle_sleep_seconds: toNumber(systemConfig.master_import_manager.worker_idle_sleep_seconds, DEFAULT_MASTER_IMPORT_MANAGER.worker_idle_sleep_seconds),
        batch_completion_grace_seconds: toNumber(systemConfig.master_import_manager.batch_completion_grace_seconds, DEFAULT_MASTER_IMPORT_MANAGER.batch_completion_grace_seconds),
        continuous_batch_pause_seconds: toNumber(systemConfig.master_import_manager.continuous_batch_pause_seconds, DEFAULT_MASTER_IMPORT_MANAGER.continuous_batch_pause_seconds),
        queue_folder_batch_size: toNumber(systemConfig.master_import_manager.queue_folder_batch_size, DEFAULT_MASTER_IMPORT_MANAGER.queue_folder_batch_size),
        queue_folder_scan_concurrency: toNumber(systemConfig.master_import_manager.queue_folder_scan_concurrency, DEFAULT_MASTER_IMPORT_MANAGER.queue_folder_scan_concurrency),
        queue_folder_shutdown_threshold_minutes: toNumber(systemConfig.master_import_manager.queue_folder_shutdown_threshold_minutes, DEFAULT_MASTER_IMPORT_MANAGER.queue_folder_shutdown_threshold_minutes),
        status_broadcast_min_interval_seconds: toNumber(systemConfig.master_import_manager.status_broadcast_min_interval_seconds, DEFAULT_MASTER_IMPORT_MANAGER.status_broadcast_min_interval_seconds)
      }
    };

    sendCommand('update_configuration', payload)
      .then(() => sendCommand('get_performance_settings'))
      .catch((error) => {
        openSnackbar(
          error?.message || t('global_setting.save_failed', 'Save failed'),
          5000
        );
      });
  };

  const renderNumberField = (section, field, label, helperText, fallback, min = 0, step = 1) => (
    <TextField
      label={t(`global_setting.${field}`, label)}
      value={systemConfig[section] ? systemConfig[section][field] : systemConfig[field] ?? fallback}
      onChange={section ? updateSectionField(section, field, true) : updateField(field)}
      fullWidth
      type="number"
      inputProps={{ min, step }}
      helperText={helperText}
      sx={{ mt: 1.5 }}
    />
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" gutterBottom>
            {t('global_setting.system_section', 'System & Runtime')}
          </Typography>
          <Tooltip
            title={t('global_setting.system_section_tip', 'Expose operational settings that affect ingestion and post-processing. Secrets and path-sensitive storage settings remain in config.toml only.')}
            placement="top"
          >
            <InfoOutlinedIcon fontSize="small" sx={{ cursor: 'pointer' }} />
          </Tooltip>
        </Box>
      </Grid>

      <Grid item xs={12} md={6}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {t('global_setting.system_access_title', 'Access and feature toggles')}
        </Typography>
        {renderToggleWithHelp(
          'allowUserPluginGeneration',
          'global_setting.allow_user_plugin_generation',
          'Allow users to generate plugins',
          'global_setting.allow_user_plugin_generation_help',
          'Lets non-admin users create plugins from the UI.'
        )}
        {renderToggleWithHelp(
          'paymentsEnabled',
          'global_setting.payments_enabled',
          'Enable payments',
          'global_setting.payments_enabled_help',
          'Controls whether credit purchases are shown and accepted.'
        )}
        {renderToggleWithHelp(
          'showWidgetEvidence',
          'global_setting.show_widget_evidence',
          'Show widget evidence',
          'global_setting.show_widget_evidence_help',
          'Shows the evidence dialogs and related widget proof details.'
        )}
        {renderToggleWithHelp(
          'restrictToUserFolder',
          'global_setting.restrict_to_user_folder',
          'Restrict to user folder',
          'global_setting.restrict_to_user_folder_help',
          'Limits file browsing and downloads to the user folder.'
        )}

        <TextField
          label={t('global_setting.allowed_connectors', 'Allowed connectors')}
          value={systemConfig.allowedConnectorsText}
          onChange={updateField('allowedConnectorsText')}
          fullWidth
          multiline
          minRows={2}
          helperText={t('global_setting.allowed_connectors_helper', 'Comma-separated connector names. Leave empty to allow all connectors.')}
          sx={{ mt: 2 }}
        />
        <TextField
          label={t('global_setting.disabled_connectors', 'Disabled connectors')}
          value={systemConfig.disabledConnectorsText}
          onChange={updateField('disabledConnectorsText')}
          fullWidth
          multiline
          minRows={2}
          helperText={t('global_setting.disabled_connectors_helper', 'Comma-separated connector names that must stay disabled.')}
          sx={{ mt: 2 }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {t('global_setting.runtime_limits_title', 'AI and runtime limits')}
        </Typography>
        {renderNumberField(null, 'searchThreshold', 'Search threshold', t('global_setting.search_threshold_helper', 'Higher values make search stricter.'), 1.4, 0, 0.1)}
        {renderNumberField(null, 'aiVisionMaxRetries', 'Vision retries', t('global_setting.ai_vision_max_retries_helper', 'Maximum retry count for vision calls.'), 5, 0, 1)}
        {renderNumberField(null, 'maxAudioRecordingLengthSeconds', 'Max audio recording length', t('global_setting.max_audio_recording_length_seconds_helper', 'Maximum client-side audio duration in seconds.'), 7200, 1, 1)}
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ my: 1 }} />
      </Grid>

      <Grid item xs={12} md={6}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {t('global_setting.postprocessing_section', 'Post-processing runtime')}
        </Typography>
        {renderNumberField('postprocessing', 'action_timeout_seconds', 'Action timeout (seconds)', t('global_setting.action_timeout_seconds_helper', 'Timeout for one built-in post-processing action.'), DEFAULT_POSTPROCESSING.action_timeout_seconds)}
        {renderNumberField('postprocessing', 'subprocess_loop_timeout_seconds', 'Subprocess loop timeout (seconds)', t('global_setting.subprocess_loop_timeout_seconds_helper', 'Timeout for the internal subprocess-compatible event loop.'), DEFAULT_POSTPROCESSING.subprocess_loop_timeout_seconds)}
        <TextField
          label={t('global_setting.pdf_render_node_path', 'PDF render Node.js path')}
          value={systemConfig.postprocessing.pdf_render_node_path}
          onChange={updateSectionField('postprocessing', 'pdf_render_node_path')}
          fullWidth
          helperText={t('global_setting.pdf_render_node_path_helper', 'Optional Node executable for PDF rendering. Leave empty to use PATH.')}
          sx={{ mt: 2 }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {t('global_setting.master_import_section', 'Ingestion limits')}
        </Typography>
        {renderNumberField('master_import_manager', 'file_process_timeout_seconds', 'File process timeout (seconds)', t('global_setting.file_process_timeout_seconds_helper', 'Per-file timeout used by the import manager.'), DEFAULT_MASTER_IMPORT_MANAGER.file_process_timeout_seconds)}
        {renderNumberField('master_import_manager', 'max_time_to_process_batch', 'Max batch process time', t('global_setting.max_time_to_process_batch_helper', 'Maximum processing window for a batch of files.'), DEFAULT_MASTER_IMPORT_MANAGER.max_time_to_process_batch)}
        {renderNumberField('master_import_manager', 'max_files_in_batch', 'Max files in batch', t('global_setting.max_files_in_batch_helper', 'Upper limit for one ingestion batch.'), DEFAULT_MASTER_IMPORT_MANAGER.max_files_in_batch)}
        {renderNumberField('master_import_manager', 'max_workers', 'Max workers', t('global_setting.max_workers_helper', 'Worker pool size for ingestion processing.'), DEFAULT_MASTER_IMPORT_MANAGER.max_workers)}
        {renderNumberField('master_import_manager', 'max_semaphore', 'Max semaphore', t('global_setting.max_semaphore_helper', 'Semaphore limit for ingestion concurrency.'), DEFAULT_MASTER_IMPORT_MANAGER.max_semaphore)}
        {renderNumberField('master_import_manager', 'worker_status_log_interval_seconds', 'Worker status log interval', t('global_setting.worker_status_log_interval_seconds_helper', 'How often worker status is logged.'), DEFAULT_MASTER_IMPORT_MANAGER.worker_status_log_interval_seconds)}
        {renderNumberField('master_import_manager', 'worker_batch_check_interval_seconds', 'Worker batch check interval', t('global_setting.worker_batch_check_interval_seconds_helper', 'Interval between batch checks.'), DEFAULT_MASTER_IMPORT_MANAGER.worker_batch_check_interval_seconds)}
        {renderNumberField('master_import_manager', 'worker_idle_sleep_seconds', 'Worker idle sleep', t('global_setting.worker_idle_sleep_seconds_helper', 'Sleep duration when the worker is idle.'), DEFAULT_MASTER_IMPORT_MANAGER.worker_idle_sleep_seconds, 0, 0.1)}
        {renderNumberField('master_import_manager', 'batch_completion_grace_seconds', 'Batch completion grace', t('global_setting.batch_completion_grace_seconds_helper', 'Grace window before batch completion is finalized.'), DEFAULT_MASTER_IMPORT_MANAGER.batch_completion_grace_seconds, 0, 0.1)}
        {renderNumberField('master_import_manager', 'continuous_batch_pause_seconds', 'Continuous batch pause', t('global_setting.continuous_batch_pause_seconds_helper', 'Pause between continuous batch runs.'), DEFAULT_MASTER_IMPORT_MANAGER.continuous_batch_pause_seconds, 0, 0.1)}
        {renderNumberField('master_import_manager', 'queue_folder_batch_size', 'Queue folder batch size', t('global_setting.queue_folder_batch_size_helper', 'Maximum files scanned per queue-folder batch.'), DEFAULT_MASTER_IMPORT_MANAGER.queue_folder_batch_size)}
        {renderNumberField('master_import_manager', 'queue_folder_scan_concurrency', 'Queue folder scan concurrency', t('global_setting.queue_folder_scan_concurrency_helper', 'Number of concurrent queue-folder scanners.'), DEFAULT_MASTER_IMPORT_MANAGER.queue_folder_scan_concurrency)}
        {renderNumberField('master_import_manager', 'queue_folder_shutdown_threshold_minutes', 'Queue folder shutdown threshold', t('global_setting.queue_folder_shutdown_threshold_minutes_helper', 'How long a queue folder may stay stale before shutdown.'), DEFAULT_MASTER_IMPORT_MANAGER.queue_folder_shutdown_threshold_minutes, 0, 1)}
        {renderNumberField('master_import_manager', 'status_broadcast_min_interval_seconds', 'Status broadcast interval', t('global_setting.status_broadcast_min_interval_seconds_helper', 'Minimum interval between status broadcasts.'), DEFAULT_MASTER_IMPORT_MANAGER.status_broadcast_min_interval_seconds, 0, 0.1)}
      </Grid>

      <Grid item xs={12} sx={{ mt: 2, textAlign: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={!isModified}
          sx={{
            backgroundColor: '#5757b0',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#aaaaca',
            },
            py: 1,
            px: 3,
            borderRadius: 2,
          }}
        >
          {t('global_setting.save_button') || 'Save System Settings'}
        </Button>
      </Grid>
    </Grid>
  );
};

export { SystemSettings };
